import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { MessageSquare, X, Send, Sparkles, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function BidChatPanel({ rfpId, rfpTitle }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get(`/rfps/${rfpId}/chat`).then(({ data }) => setMessages(data.messages || [])).catch(() => {});
  }, [open, rfpId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages(m => [...m, { role: "user", content: text, ts: new Date().toISOString() }]);
    try {
      const { data } = await api.post(`/rfps/${rfpId}/chat`, { message: text });
      setMessages(data.messages || []);
    } catch { toast.error("Chat failed"); }
    finally { setSending(false); }
  };

  const clearChat = async () => {
    if (!window.confirm("Clear this conversation?")) return;
    try {
      await api.delete(`/rfps/${rfpId}/chat`);
      setMessages([]);
      toast.success("Chat cleared");
    } catch {}
  };

  const suggestions = [
    "Which vendor is the cheapest after compliance?",
    "What are the biggest HSE risks on this bid?",
    "Summarize the comparison in EUR.",
  ];

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen(true)}
        data-testid="chat-launcher"
        className="fixed bottom-24 end-6 w-14 h-14 rounded-full btn-tactile btn-amber inline-flex items-center justify-center shadow-2xl z-40"
        title="AI Bid Assistant"
      >
        <MessageSquare className="w-6 h-6" />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-black/60 z-40"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed end-0 top-0 bottom-0 w-full sm:w-[460px] bg-black border-s border-white/10 z-50 flex flex-col"
              data-testid="chat-panel"
            >
              {/* Header */}
              <div className="border-b border-white/10 p-4 flex items-center justify-between bg-black/70 backdrop-blur">
                <div className="flex items-center gap-3 min-w-0">
                  <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-mono-data text-[10px] tracking-widest uppercase text-amber-500">AI Bid Assistant</div>
                    <div className="font-display text-base truncate">{rfpTitle}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {messages.length > 0 && (
                    <button onClick={clearChat} data-testid="chat-clear-btn" className="text-text-secondary hover:text-red-400" title="Clear">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => setOpen(false)} data-testid="chat-close-btn" className="text-text-secondary hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <Sparkles className="w-10 h-10 text-amber-500/60 mx-auto mb-3" />
                    <div className="font-display text-lg">Ask anything about this bid</div>
                    <div className="text-xs text-text-secondary mb-4">I have full context — vendors, HSE, compliance, pricing.</div>
                    <div className="space-y-2">
                      {suggestions.map(s => (
                        <button
                          key={s}
                          onClick={() => setInput(s)}
                          data-testid={`chat-suggestion-${s.slice(0,10)}`}
                          className="tray-inset block w-full text-start px-3 py-2 text-xs hover:ring-1 hover:ring-amber-500/40 transition"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[85%] px-4 py-2.5 rounded-lg text-sm leading-relaxed ${
                          m.role === "user"
                            ? "bg-amber-500 text-black"
                            : "tray-inset"
                        }`}
                      >
                        {m.content}
                      </div>
                    </div>
                  ))
                )}
                {sending && (
                  <div className="flex justify-start">
                    <div className="tray-inset px-4 py-2.5 inline-flex items-center gap-2 text-xs text-text-secondary">
                      <Loader2 className="w-3 h-3 animate-spin" /> thinking...
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="border-t border-white/10 p-3 bg-black/70 backdrop-blur">
                <div className="tray-inset flex items-center gap-2 px-3 py-2">
                  <input
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && send()}
                    placeholder="Ask the AI..."
                    data-testid="chat-input"
                    disabled={sending}
                    className="flex-1 bg-transparent text-sm outline-none"
                  />
                  <button onClick={send} disabled={!input.trim() || sending} data-testid="chat-send-btn" className="text-amber-500 disabled:opacity-30">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
